import { Avatar, AvatarFallback, AvatarImage } from "@/components/ui/avatar"
import { Badge } from "@/components/ui/badge"
import {
  Breadcrumb,
  BreadcrumbItem,
  BreadcrumbLink,
  BreadcrumbList,
  BreadcrumbPage,
  BreadcrumbSeparator,
} from "@/components/ui/breadcrumb"
import { Button } from "@/components/ui/button"
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card"
import { Separator } from "@/components/ui/separator"
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from "@/components/ui/table"
import AppLayout from "@/layouts/AppLayout"
import { Head, useForm, usePage } from "@inertiajs/react"
import { useState } from "react"

interface Balance {
  balance_type: string
  year: number
  days_available: number
  days_used: number
  remaining_days: number
}

interface SalaryHistoryRow {
  effective_date: string
  seniority_title: string
  salary: string
  salary_change: string
  reason: string
}

interface TimeOffRow {
  id: number
  request_type: string
  days_requested: number
  start_date: string
  end_date: string
  status: string
}

interface UserDetail {
  id: number
  name: string
  email: string
  role: string
  avatar_url: string | null
  hire_date: string | null
  national_id: string | null
  rut: string | null
  date_of_birth: string | null
  current_salary_display: string
  seniority_title: string
  contract_hours_per_day: number
  has_api_token: boolean
  balances: Balance[]
  balance_summary: { total_available: number; total_used: number; total_remaining: number }
  salary_histories: SalaryHistoryRow[]
  recent_requests: TimeOffRow[]
}

interface Props {
  [key: string]: unknown
  user: UserDetail
  flash: { api_token?: string }
}

const statusColors: Record<string, string> = {
  pending: "bg-yellow-100 text-yellow-800 dark:bg-yellow-900/30 dark:text-yellow-400",
  approved: "bg-green-100 text-green-800 dark:bg-green-900/30 dark:text-green-400",
  denied: "bg-red-100 text-red-800 dark:bg-red-900/30 dark:text-red-400",
  cancelled: "bg-muted text-muted-foreground",
}

function Detail({ label, value }: { label: string; value: React.ReactNode }) {
  return (
    <div>
      <dt className="text-xs font-medium uppercase tracking-wider text-muted-foreground">{label}</dt>
      <dd className="mt-1 text-sm font-medium">{value ?? "—"}</dd>
    </div>
  )
}

function ApiAccessCard({ hasToken, revealedToken }: { hasToken: boolean; revealedToken?: string }) {
  const { post, processing } = useForm({})
  const [copied, setCopied] = useState(false)

  function generate() {
    post("/profile/api_token", { preserveScroll: true })
  }

  function copy() {
    if (!revealedToken) return
    navigator.clipboard.writeText(revealedToken).then(() => {
      setCopied(true)
      setTimeout(() => setCopied(false), 2000)
    })
  }

  return (
    <Card>
      <CardHeader>
        <CardTitle>API access</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        <p className="text-sm text-muted-foreground">
          Personal access token for the Ops Center API (e.g. to upload time entries from scripts). Send it as{" "}
          <code className="rounded bg-muted px-1 py-0.5 text-xs">Authorization: Bearer &lt;token&gt;</code>.
        </p>

        {revealedToken ? (
          <div className="rounded-md border border-green-200 bg-green-50 p-3 dark:border-green-900/40 dark:bg-green-900/20">
            <p className="text-xs font-medium text-green-800 dark:text-green-400">
              Copy this token now — it won't be shown again.
            </p>
            <div className="mt-2 flex items-center gap-2">
              <code className="flex-1 break-all rounded bg-background px-2 py-1 font-mono text-sm">
                {revealedToken}
              </code>
              <Button type="button" variant="outline" size="sm" onClick={copy}>
                {copied ? "Copied" : "Copy"}
              </Button>
            </div>
          </div>
        ) : (
          <p className="text-sm">
            {hasToken ? "A token is active. Regenerating will invalidate the previous token." : "No token yet."}
          </p>
        )}

        <Button type="button" onClick={generate} disabled={processing}>
          {processing ? "Generating…" : hasToken ? "Regenerate token" : "Generate token"}
        </Button>
      </CardContent>
    </Card>
  )
}

export default function ProfileShow() {
  const { user, flash } = usePage<Props>().props
  const initials = user.name
    .split(" ")
    .map((s) => s[0])
    .join("")
    .slice(0, 2)
    .toUpperCase()

  return (
    <>
      <Head title="My Profile - Operations Center" />
      <div className="flex flex-1 flex-col gap-6 p-4 lg:p-6">
        <Breadcrumb>
          <BreadcrumbList>
            <BreadcrumbItem>
              <BreadcrumbLink href="/">Home</BreadcrumbLink>
            </BreadcrumbItem>
            <BreadcrumbSeparator />
            <BreadcrumbItem>
              <BreadcrumbPage>My Profile</BreadcrumbPage>
            </BreadcrumbItem>
          </BreadcrumbList>
        </Breadcrumb>

        <Card>
          <CardContent className="p-6">
            <div className="flex items-start gap-4">
              <Avatar className="h-16 w-16">
                {user.avatar_url ? <AvatarImage src={user.avatar_url} alt={user.name} /> : null}
                <AvatarFallback>{initials}</AvatarFallback>
              </Avatar>
              <div className="flex-1">
                <h1 className="text-2xl font-semibold">{user.name}</h1>
                <p className="text-muted-foreground">{user.email}</p>
                <div className="mt-2 flex items-center gap-2">
                  <Badge variant="outline" className="capitalize">
                    {user.role}
                  </Badge>
                  <Badge variant="secondary">{user.seniority_title}</Badge>
                </div>
              </div>
              <div className="text-right">
                <p className="text-xs uppercase text-muted-foreground tracking-wider">Current salary</p>
                <p className="text-xl font-semibold tabular-nums">{user.current_salary_display}</p>
              </div>
            </div>
            <Separator className="my-6" />
            <dl className="grid grid-cols-2 gap-6 sm:grid-cols-3">
              <Detail label="Hire date" value={user.hire_date} />
              <Detail label="Contract hours/day" value={user.contract_hours_per_day} />
              <Detail label="National ID" value={user.national_id} />
              <Detail label="RUT" value={user.rut} />
              <Detail label="Date of birth" value={user.date_of_birth} />
            </dl>
          </CardContent>
        </Card>

        <ApiAccessCard hasToken={user.has_api_token} revealedToken={flash?.api_token} />

        <Card>
          <CardHeader>
            <CardTitle>Time off balances</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Year</TableHead>
                  <TableHead className="text-right">Available</TableHead>
                  <TableHead className="text-right">Used</TableHead>
                  <TableHead className="text-right">Remaining</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {user.balances.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-6">
                      No balances yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  user.balances.map((b) => (
                    <TableRow key={`${b.balance_type}-${b.year}`}>
                      <TableCell className="capitalize">{b.balance_type}</TableCell>
                      <TableCell>{b.year}</TableCell>
                      <TableCell className="text-right tabular-nums">{b.days_available}</TableCell>
                      <TableCell className="text-right tabular-nums">{b.days_used}</TableCell>
                      <TableCell className="text-right tabular-nums">{b.remaining_days}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Salary history</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Effective</TableHead>
                  <TableHead>Level</TableHead>
                  <TableHead className="text-right">Salary</TableHead>
                  <TableHead>Change</TableHead>
                  <TableHead>Reason</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {user.salary_histories.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={5} className="text-center text-muted-foreground py-6">
                      No salary history.
                    </TableCell>
                  </TableRow>
                ) : (
                  user.salary_histories.map((sh, i) => (
                    <TableRow key={`${sh.effective_date}-${i}`}>
                      <TableCell className="tabular-nums">{sh.effective_date}</TableCell>
                      <TableCell>{sh.seniority_title}</TableCell>
                      <TableCell className="text-right tabular-nums">{sh.salary}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{sh.salary_change}</TableCell>
                      <TableCell className="text-sm text-muted-foreground">{sh.reason}</TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>

        <Card>
          <CardHeader>
            <CardTitle>Recent time off requests</CardTitle>
          </CardHeader>
          <CardContent>
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Type</TableHead>
                  <TableHead>Dates</TableHead>
                  <TableHead className="text-right">Days</TableHead>
                  <TableHead>Status</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {user.recent_requests.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center text-muted-foreground py-6">
                      No requests yet.
                    </TableCell>
                  </TableRow>
                ) : (
                  user.recent_requests.map((r) => (
                    <TableRow key={r.id}>
                      <TableCell className="capitalize">{r.request_type}</TableCell>
                      <TableCell className="tabular-nums text-sm">
                        {r.start_date} – {r.end_date}
                      </TableCell>
                      <TableCell className="text-right tabular-nums">{r.days_requested}</TableCell>
                      <TableCell>
                        <span
                          className={`inline-flex rounded-full px-2 py-0.5 text-xs ${statusColors[r.status] ?? ""}`}
                        >
                          {r.status}
                        </span>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </CardContent>
        </Card>
      </div>
    </>
  )
}

ProfileShow.layout = (page: React.ReactNode) => <AppLayout>{page}</AppLayout>
